import {
    AccountInfo,
    IPublicClientApplication,
    InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { loginRequest, graphConfig } from "@/config/msal-config";
import {
    clearMsalCacheArtifacts,
    clearStoredMsalSessionHint,
} from "@/services/msalSessionState";

export interface UserProfile {
    id: string;
    email: string;
    name: string;
    firstName: string;
    role: string;
    organization: string;
}

export class AuthService {
    private msalInstance: IPublicClientApplication;

    constructor(msalInstance: IPublicClientApplication) {
        this.msalInstance = msalInstance;
    }

    getActiveAccount(): AccountInfo | null {
        const activeAccount = this.msalInstance.getActiveAccount();
        if (!activeAccount && this.msalInstance.getAllAccounts().length > 0) {
            const accounts = this.msalInstance.getAllAccounts();
            this.msalInstance.setActiveAccount(accounts[0]);
            return accounts[0];
        }
        return activeAccount;
    }

    async login(email?: string, password?: string): Promise<void> {
        if (email && password) {
            // Direct login implementation
            // TODO: Implement direct login with your backend
            throw new Error("Direct login not implemented");
        } else {
            // MSAL login
            const accounts = this.msalInstance.getAllAccounts();
            if (accounts.length === 0) {
                await this.msalInstance.loginRedirect(loginRequest);
            } else {
                this.msalInstance.setActiveAccount(accounts[0]);
            }
        }
    }

    async handleRedirectResponse() {
        return await this.msalInstance.handleRedirectPromise();
    }

    // Clear MSAL client state and let higher-level flows own the redirect.
    logout(): void {
        // 1. Clear active account
        this.msalInstance.setActiveAccount(null);

        // 2. Clear any stored tokens in cache
        try {
            this.msalInstance.clearCache();
        } catch (e) {
            console.error("Error clearing cache", e);
        }

        // 3. Clear any session storage
        sessionStorage.clear();
        try {
            clearMsalCacheArtifacts(localStorage);
            clearStoredMsalSessionHint();
        } catch (e) {
            console.error("Error clearing storage", e);
        }
    }

    async getUserProfile(): Promise<UserProfile | null> {
        const account = this.getActiveAccount();
        if (!account) return null;
        try {
            const tokenResponse = await this.msalInstance.acquireTokenSilent({
                ...loginRequest,
                account,
            });
            const res = await fetch(graphConfig.graphMeEndpoint, {
                headers: {
                    Authorization: `Bearer ${tokenResponse.accessToken}`,
                },
            });
            const profile = await res.json();
            // Map/validate profile fields as needed
            return {
                id: profile.id,
                email: profile.mail || profile.userPrincipalName || "",
                name: profile.displayName || "",
                firstName: profile.givenName || "",
                role: profile.jobTitle || "",
                organization: profile.companyName || "",
            };
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                await this.msalInstance.acquireTokenRedirect({
                    ...loginRequest,
                    account,
                });
            }
            throw error;
        }
    }
}

export default AuthService;
